<template>
  <div>
    <!-- Keyboard Shortcut Trigger -->
    <div v-if="isOpen" class="smart-search-overlay" @click="close" :class="{ 'dark-mode': isDarkMode }">
      <div class="smart-search-modal" @click.stop :class="{ 'dark-mode': isDarkMode }">
        <!-- Search Input -->
        <div class="search-header">
          <i class="cil-magnifying-glass search-icon"></i>
          <input
            ref="searchInput"
            v-model="query"
            @input="handleInput"
            @keydown="handleKeydown"
            type="text"
            placeholder="Search features, actions, or type a question..."
            class="search-input"
            autocomplete="off"
          />
          <button @click="close" class="close-btn">
            <i class="cil-x"></i>
          </button>
        </div>

        <!-- Search Results -->
        <div v-if="query.length > 0" class="search-results">
          <!-- Loading State -->
          <div v-if="isLoading" class="loading-state">
            <div class="spinner"></div>
            <p>Searching...</p>
          </div>

          <!-- Results -->
          <div v-else-if="hasResults" class="results-container">
            <!-- Features -->
            <div v-if="results.features && results.features.length > 0" class="result-section">
              <h4 class="section-title">Features</h4>
              <div
                v-for="(feature, index) in results.features"
                :key="`feature-${index}`"
                @click="navigateTo(feature.route)"
                @mouseenter="selectedIndex = getAbsoluteIndex('features', index)"
                class="result-item"
                :class="{ selected: selectedIndex === getAbsoluteIndex('features', index) }"
              >
                <i :class="feature.icon" class="result-icon"></i>
                <div class="result-content">
                  <div class="result-name">{{ feature.name }}</div>
                  <div class="result-description">{{ feature.description }}</div>
                </div>
                <span class="result-category">{{ feature.category }}</span>
              </div>
            </div>

            <!-- Actions -->
            <div v-if="results.actions && results.actions.length > 0" class="result-section">
              <h4 class="section-title">Actions</h4>
              <div
                v-for="(action, index) in results.actions"
                :key="`action-${index}`"
                @click="executeAction(action)"
                @mouseenter="selectedIndex = getAbsoluteIndex('actions', index)"
                class="result-item"
                :class="{ selected: selectedIndex === getAbsoluteIndex('actions', index) }"
              >
                <i :class="action.icon" class="result-icon"></i>
                <div class="result-content">
                  <div class="result-name">{{ action.name }}</div>
                  <div class="result-description">{{ action.description }}</div>
                </div>
                <i class="cil-arrow-right result-arrow"></i>
              </div>
            </div>

            <!-- Help Articles -->
            <div v-if="results.help_articles && results.help_articles.length > 0" class="result-section">
              <h4 class="section-title">Help & Documentation</h4>
              <div
                v-for="(article, index) in results.help_articles"
                :key="`article-${index}`"
                @click="openArticle(article)"
                @mouseenter="selectedIndex = getAbsoluteIndex('help_articles', index)"
                class="result-item"
                :class="{ selected: selectedIndex === getAbsoluteIndex('help_articles', index) }"
              >
                <i :class="article.icon" class="result-icon"></i>
                <div class="result-content">
                  <div class="result-name">{{ article.title }}</div>
                  <div class="result-description">{{ article.description }}</div>
                </div>
                <span class="result-category">{{ article.category }}</span>
              </div>
            </div>

            <!-- Quick Actions from Results -->
            <div v-if="results.quick_actions && results.quick_actions.length > 0" class="result-section">
              <h4 class="section-title">Quick Actions</h4>
              <div class="quick-actions-grid">
                <button
                  v-for="(action, index) in results.quick_actions"
                  :key="`quick-${index}`"
                  @click="executeQuickAction(action)"
                  class="quick-action-btn"
                >
                  <i :class="action.icon"></i>
                  <span>{{ action.label }}</span>
                </button>
              </div>
            </div>

            <!-- AI Suggestions -->
            <div v-if="results.suggestions && results.suggestions.length > 0" class="result-section">
              <h4 class="section-title">You might also try</h4>
              <div class="suggestions-list">
                <button
                  v-for="(suggestion, index) in results.suggestions"
                  :key="`suggestion-${index}`"
                  @click="query = suggestion"
                  class="suggestion-btn"
                >
                  {{ suggestion }}
                </button>
              </div>
            </div>
          </div>

          <!-- No Results -->
          <div v-else class="no-results">
            <i class="cil-search-X no-results-icon"></i>
            <p>No results found for "{{ query }}"</p>
            <button @click="askAI" class="ask-ai-btn">
              <i class="cil-comment-bubble"></i>
              Ask AI Assistant
            </button>
          </div>
        </div>

        <!-- Default View (Empty Query) -->
        <div v-else class="default-view">
          <!-- Recent Searches -->
          <div v-if="recentSearches.length > 0" class="default-section">
            <h4 class="section-title">Recent Searches</h4>
            <div class="recent-searches">
              <button
                v-for="(search, index) in recentSearches"
                :key="`recent-${index}`"
                @click="query = search"
                class="recent-search-item"
              >
                <i class="cil-history"></i>
                {{ search }}
              </button>
            </div>
          </div>

          <!-- Quick Actions -->
          <div class="default-section">
            <h4 class="section-title">Quick Actions</h4>
            <div class="quick-actions-grid">
              <button
                v-for="(action, index) in quickActions"
                :key="`default-quick-${index}`"
                @click="executeQuickAction(action)"
                class="quick-action-btn"
              >
                <i :class="action.icon"></i>
                <span>{{ action.label }}</span>
              </button>
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div class="search-footer">
          <div class="keyboard-hints">
            <span class="hint">
              <kbd>↑↓</kbd> Navigate
            </span>
            <span class="hint">
              <kbd>Enter</kbd> Select
            </span>
            <span class="hint">
              <kbd>Esc</kbd> Close
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import smartSearchService from '@/services/smartSearchService';

export default {
  name: 'SmartSearch',

  data() {
    return {
      isOpen: false,
      query: '',
      results: {},
      isLoading: false,
      selectedIndex: 0,
      searchTimeout: null,
      quickActions: [],
      recentSearches: [],
    };
  },

  computed: {
    isDarkMode() {
      return this.$store.state.darkMode;
    },

    hasResults() {
      return (
        (this.results.features && this.results.features.length > 0) ||
        (this.results.actions && this.results.actions.length > 0) ||
        (this.results.help_articles && this.results.help_articles.length > 0)
      );
    },

    totalResults() {
      let total = 0;
      if (this.results.features) total += this.results.features.length;
      if (this.results.actions) total += this.results.actions.length;
      if (this.results.help_articles) total += this.results.help_articles.length;
      return total;
    },
  },

  mounted() {
    // Register global keyboard shortcut (Cmd+K / Ctrl+K)
    document.addEventListener('keydown', this.handleGlobalKeydown);
    
    // Load quick actions and recent searches
    this.quickActions = smartSearchService.getQuickActions();
    this.recentSearches = smartSearchService.getRecentSearches();
  },

  beforeDestroy() {
    document.removeEventListener('keydown', this.handleGlobalKeydown);
  },

  methods: {
    open() {
      this.isOpen = true;
      this.$nextTick(() => {
        this.$refs.searchInput?.focus();
      });
    },

    close() {
      this.isOpen = false;
      this.query = '';
      this.results = {};
      this.selectedIndex = 0;
    },

    handleGlobalKeydown(event) {
      // Cmd+K (Mac) or Ctrl+K (Windows/Linux)
      if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
        event.preventDefault();
        if (this.isOpen) {
          this.close();
        } else {
          this.open();
        }
      }
    },

    handleInput() {
      clearTimeout(this.searchTimeout);
      this.selectedIndex = 0;

      if (this.query.length === 0) {
        this.results = {};
        this.isLoading = false;
        return;
      }

      this.isLoading = true;

      this.searchTimeout = setTimeout(async () => {
        await this.performSearch();
      }, 300); // Debounce 300ms
    },

    async performSearch() {
      try {
        const searchResults = await smartSearchService.search(this.query, {
          page: this.$route?.name,
          page_url: window.location.pathname,
        });

        this.results = searchResults;
      } catch (error) {
        console.error('[SmartSearch] Search failed:', error);
      } finally {
        this.isLoading = false;
      }
    },

    handleKeydown(event) {
      if (event.key === 'Escape') {
        this.close();
      } else if (event.key === 'ArrowDown') {
        event.preventDefault();
        this.selectedIndex = Math.min(this.selectedIndex + 1, this.totalResults - 1);
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        this.selectedIndex = Math.max(this.selectedIndex - 1, 0);
      } else if (event.key === 'Enter') {
        event.preventDefault();
        this.selectCurrentItem();
      }
    },

    getAbsoluteIndex(section, index) {
      let absoluteIndex = 0;
      
      if (section === 'features') {
        return index;
      }
      
      absoluteIndex += this.results.features?.length || 0;
      
      if (section === 'actions') {
        return absoluteIndex + index;
      }
      
      absoluteIndex += this.results.actions?.length || 0;
      
      if (section === 'help_articles') {
        return absoluteIndex + index;
      }
      
      return absoluteIndex;
    },

    selectCurrentItem() {
      const featureCount = this.results.features?.length || 0;
      const actionCount = this.results.actions?.length || 0;

      if (this.selectedIndex < featureCount) {
        const feature = this.results.features[this.selectedIndex];
        this.navigateTo(feature.route);
      } else if (this.selectedIndex < featureCount + actionCount) {
        const action = this.results.actions[this.selectedIndex - featureCount];
        this.executeAction(action);
      } else {
        const article = this.results.help_articles[this.selectedIndex - featureCount - actionCount];
        this.openArticle(article);
      }
    },

    navigateTo(route) {
      if (route) {
        this.$router.push(route);
        this.close();
      }
    },

    executeAction(action) {
      if (action.route) {
        this.navigateTo(action.route);
      } else if (action.action) {
        // Execute custom action
        this.$emit('execute-action', action);
        this.close();
      }
    },

    executeQuickAction(action) {
      if (action.type === 'navigate' && action.route) {
        this.navigateTo(action.route);
      } else if (action.type === 'open_ai') {
        this.close();
        this.$store.dispatch('aiAssistant/open');
      } else if (action.action) {
        this.$emit('execute-action', action);
        this.close();
      }
    },

    openArticle(article) {
      if (article.url) {
        window.open(article.url, '_blank');
      }
      this.close();
    },

    askAI() {
      // Validate query before sending
      if (!this.query || this.query.trim().length === 0) {
        return;
      }

      this.close();
      this.$store.dispatch('aiAssistant/open');
      
      // Use $nextTick to ensure store is ready
      this.$nextTick(() => {
        this.$store.dispatch('aiAssistant/sendMessage', {
          message: this.query.trim(),
          context: {
            from: 'smart_search',
            page: this.$route?.name,
          },
        }).catch(error => {
          console.error('[SmartSearch] Failed to send message to AI:', error);
        });
      });
    },
  },
};
</script>

<style scoped lang="scss">
.smart-search-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  z-index: 10000;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding-top: 15vh;
  animation: fadeIn 0.2s ease;

  &.dark-mode {
    background: rgba(0, 0, 0, 0.7);
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.smart-search-modal {
  width: 90%;
  max-width: 640px;
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 16px 64px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  max-height: 70vh;
  animation: slideDown 0.3s ease;

  &.dark-mode {
    background: #1e1e1e;
    color: #e0e0e0;
  }
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.search-header {
  display: flex;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #e0e0e0;

  .dark-mode & {
    border-bottom-color: #3a3a3a;
  }

  .search-icon {
    font-size: 20px;
    color: #667eea;
    margin-right: 12px;
  }

  .search-input {
    flex: 1;
    border: none;
    outline: none;
    font-size: 16px;
    background: transparent;
    color: inherit;

    &::placeholder {
      color: #999;
    }
  }

  .close-btn {
    background: none;
    border: none;
    cursor: pointer;
    padding: 8px;
    border-radius: 4px;
    transition: background 0.2s ease;

    &:hover {
      background: #f5f5f5;

      .dark-mode & {
        background: #2a2a2a;
      }
    }

    i {
      font-size: 18px;
    }
  }
}

.search-results,
.default-view {
  flex: 1;
  overflow-y: auto;
  padding: 12px 0;
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  gap: 16px;

  .spinner {
    width: 32px;
    height: 32px;
    border: 3px solid #e0e0e0;
    border-top-color: #667eea;
    border-radius: 50%;
    animation: spin 0.6s linear infinite;
  }

  p {
    margin: 0;
    color: #999;
  }
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.result-section,
.default-section {
  margin-bottom: 20px;

  .section-title {
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
    color: #999;
    padding: 8px 20px;
    margin: 0;
  }
}

.result-item {
  display: flex;
  align-items: center;
  padding: 12px 20px;
  cursor: pointer;
  transition: background 0.2s ease;
  gap: 12px;

  &:hover,
  &.selected {
    background: #f5f5f5;

    .dark-mode & {
      background: #2a2a2a;
    }
  }

  .result-icon {
    font-size: 20px;
    color: #667eea;
    flex-shrink: 0;
  }

  .result-content {
    flex: 1;

    .result-name {
      font-size: 14px;
      font-weight: 600;
      margin-bottom: 2px;
    }

    .result-description {
      font-size: 13px;
      color: #666;
      opacity: 0.8;

      .dark-mode & {
        color: #999;
      }
    }
  }

  .result-category {
    font-size: 11px;
    padding: 4px 8px;
    background: #e0e0e0;
    border-radius: 12px;
    color: #666;

    .dark-mode & {
      background: #3a3a3a;
      color: #999;
    }
  }

  .result-arrow {
    font-size: 16px;
    color: #999;
  }
}

.quick-actions-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 8px;
  padding: 0 20px;
}

.quick-action-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 16px 12px;
  background: #f5f5f5;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;

  .dark-mode & {
    background: #2a2a2a;
  }

  &:hover {
    background: #667eea;
    color: white;
    transform: translateY(-2px);

    i {
      color: white;
    }
  }

  i {
    font-size: 24px;
    color: #667eea;
    transition: color 0.2s ease;
  }

  span {
    font-size: 12px;
    font-weight: 600;
    text-align: center;
  }
}

.suggestions-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 0 20px;
}

.suggestion-btn {
  padding: 8px 16px;
  background: #f5f5f5;
  border: 1px solid #e0e0e0;
  border-radius: 20px;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s ease;

  .dark-mode & {
    background: #2a2a2a;
    border-color: #3a3a3a;
  }

  &:hover {
    border-color: #667eea;
    color: #667eea;
  }
}

.no-results {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 40px 20px;
  gap: 16px;

  .no-results-icon {
    font-size: 48px;
    color: #ccc;
  }

  p {
    margin: 0;
    color: #999;
  }

  .ask-ai-btn {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px 24px;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    border: none;
    border-radius: 8px;
    font-weight: 600;
    cursor: pointer;
    transition: transform 0.2s ease;

    &:hover {
      transform: translateY(-2px);
    }
  }
}

.recent-searches {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 0 20px;
}

.recent-search-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  background: none;
  border: none;
  border-radius: 6px;
  text-align: left;
  cursor: pointer;
  transition: background 0.2s ease;
  color: inherit;

  &:hover {
    background: #f5f5f5;

    .dark-mode & {
      background: #2a2a2a;
    }
  }

  i {
    font-size: 16px;
    color: #999;
  }
}

.search-footer {
  padding: 12px 20px;
  border-top: 1px solid #e0e0e0;

  .dark-mode & {
    border-top-color: #3a3a3a;
  }

  .keyboard-hints {
    display: flex;
    gap: 16px;
    justify-content: flex-end;

    .hint {
      font-size: 12px;
      color: #999;
      display: flex;
      align-items: center;
      gap: 6px;

      kbd {
        padding: 2px 6px;
        background: #f5f5f5;
        border: 1px solid #e0e0e0;
        border-radius: 4px;
        font-size: 11px;
        font-family: monospace;

        .dark-mode & {
          background: #2a2a2a;
          border-color: #3a3a3a;
        }
      }
    }
  }
}

@media (max-width: 768px) {
  .smart-search-modal {
    width: 95%;
    max-height: 80vh;
  }

  .quick-actions-grid {
    grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
  }
}
</style>
